"use client";

import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu } from "antd";
import useLanguageStore from "../../stores/languageStore.ts";

interface SidebarProps {
  collapsed: boolean;
  onClose?: () => void;
}

// Routes definition
const ROUTES = {
  HOME: "/admin",
  DASHBOARD: "/admin/dashboard",
  USERS: "/admin/users",
  PLACES: "/admin/places",
  TAGS: "/admin/tags",
  CATEGORIES: "/admin/categories",
  ANNOUNCEMENTS: "/admin/announcements",
  ACTIVITY_GROUPS: "/admin/activity-groups",
  QUOTES: "/admin/quotes",
  TICKETS: "/admin/tickets",
  NOTIFICATIONS: "/admin/notifications",
  SETTINGS: "/admin/settings",
} as const;

export default function Sidebar({ collapsed, onClose }: SidebarProps) {
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguageStore();

  // Menu configuration with translations
  const MENU_CONFIG = useMemo(() => [
    { key: "dashboard", label: t("navigation.dashboard"), path: ROUTES.DASHBOARD },
    { key: "users", label: t("navigation.users"), path: ROUTES.USERS },
    { key: "places", label: t("navigation.places"), path: ROUTES.PLACES },
    { key: "tags", label: t("navigation.tags"), path: ROUTES.TAGS },
    { key: "categories", label: t("navigation.categories"), path: ROUTES.CATEGORIES },
    { key: "announcements", label: t("navigation.announcements"), path: ROUTES.ANNOUNCEMENTS },
    { key: "activity-groups", label: t("navigation.activityGroups"), path: ROUTES.ACTIVITY_GROUPS },
    { key: "quotes", label: t("navigation.quotes"), path: ROUTES.QUOTES },
    { key: "tickets", label: t("navigation.tickets"), path: ROUTES.TICKETS },
    { key: "notifications", label: t("navigation.notifications"), path: ROUTES.NOTIFICATIONS },
    { key: "settings", label: t("navigation.settings"), path: ROUTES.SETTINGS },
  ], [t]);

  // Responsive handling
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const activeKey = useMemo(() => {
    const item = MENU_CONFIG.find((i) => location.pathname === i.path);
    return item?.key || "";
  }, [location.pathname]);

  const handleMenuClick = (e: any) => {
    const item = MENU_CONFIG.find((i) => i.key === e.key);
    if (item) navigate(item.path);
    if (isMobile && onClose) onClose();
  };

  const menuItems = useMemo(
    () => [
      {
        key: "menu-label",
        label: !collapsed && (
          <div className="text-xs text-gray-400 uppercase px-4 py-2">Menu</div>
        ),
        type: "group" as const,
      },
      ...MENU_CONFIG.map((i) => ({
        key: i.key,
        label: !collapsed && i.label,
      })),
    ],
    [collapsed]
  );

  return (
    <>
      {/* Desktop sidebar */}
      {!isMobile && (
        <div
          className={`transition-all duration-300 bg-white border-r border-gray-100 overflow-y-auto ${collapsed ? "w-24" : "w-72"
            }`}
        >
          {/* <div className="flex items-center justify-center gap-3 p-8">
            {collapsed ? (
              <img src="/assets/images/logo-mini.png" className="max-w-[3rem]" alt="Logo Mini" />
            ) : (
              <img src="/assets/images/logo.png" className="max-w-[10rem]" alt="Logo" />
            )}
          </div> */}
          <div className="px-4">
            <Menu
              mode="inline"
              selectedKeys={[activeKey]}
              items={menuItems}
              className="border-none"
              onClick={handleMenuClick}
            />
          </div>
        </div>
      )}

      {/* Mobile sidebar */}
      {isMobile && (
        <div
          className={`z-[1] fixed inset-0 bg-black bg-opacity-40 transition-opacity duration-300 ${collapsed ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
          onClick={onClose}
        >
          <div
            className={`absolute top-0 left-0 h-full w-64 bg-white shadow-lg transform transition-transform duration-300 ${collapsed ? "-translate-x-full" : "translate-x-0"
              }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 p-6 border-b border-gray-200">
              <img src="/assets/images/logo-mini.png" className="w-10 h-10" alt="Logo Mini" />
            </div>
            <Menu
              mode="inline"
              selectedKeys={[activeKey]}
              items={menuItems}
              className="border-none"
              onClick={handleMenuClick}
            />
          </div>
        </div>
      )}
    </>
  );
}
